import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import * as XLSX from 'xlsx';
/**
 * 通用 Excel/CSV 文件解析工具函数
 * @param file 文件对象
 * @param headers 列头映射数组，如 ['material_code', 'material_name']
 * @param headerKeywords 用于检测表头的关键词数组，如 ['物料编码', '物料名称']
 * @param translate 翻译函数
 * @param localeKeys 国际化键对象
 * @param transform 可选的数据转换函数
 */
export function parseExcelFile(file, headers, headerKeywords, translate, localeKeys, transform) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const result = event.target?.result;
        const isCSV = file.name.endsWith('.csv');
        const content = isCSV ? result : result;
        const workbook = XLSX.read(content, {
          type: isCSV ? 'string' : 'array'
        });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: headers,
          raw: false,
          defval: '' // 为缺失的单元格提供默认值
        });
        if (jsonData.length === 0) {
          _Message.error(translate[localeKeys.empty]);
          reject(new Error('Empty file'));
          return;
        }

        // 检测并跳过表头
        const firstRow = jsonData[0];
        const firstRowValues = Object.values(firstRow).map(v => String(v).trim()).join('');
        const isHeader = headerKeywords.some(keyword => firstRowValues.includes(keyword));
        let dataToProcess = jsonData;
        if (isHeader) {
          dataToProcess = jsonData.slice(1);
        }

        // 过滤掉完全空的行
        dataToProcess = dataToProcess.filter(row => {
          const values = Object.values(row);
          return values.some(v => v !== undefined && v !== null && v !== '');
        });

        // 应用转换函数
        const finalData = transform ? transform(dataToProcess) : dataToProcess;
        _Message.success(translate[localeKeys.success].replace('%s', String(finalData.length)));
        resolve(finalData);
      } catch (error) {
        _Message.error(translate[localeKeys.error]);
        reject(error);
      }
    };
    reader.onerror = () => {
      _Message.error(translate[localeKeys.error]);
      reject(new Error('File read error'));
    };

    // 根据文件类型选择读取方式
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file, 'GBK');
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
}

/**
 * 通用文件选择对话框打开函数
 * @param fileInputRef 文件输入框 ref
 * @param onChange 文件选择回调
 */
export function openFileSelector(fileInputRef, onChange) {
  if (!fileInputRef.current) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = e => {
      const target = e.target;
      const file = target.files?.[0];
      if (file) {
        onChange(file);
      }
      // 清空 value 以允许重复选择同一文件
      target.value = '';
    };
    input.style.display = 'none';
    fileInputRef.current = input;
  }
  fileInputRef.current.click();
}

/**
 * Excel 日期序列号转换为日期字符串
 * @param dateValue Excel 日期值（可能是数字序列号、字符串或 Date 对象）
 */
export function formatExcelDate(dateValue) {
  if (!dateValue || dateValue === null || dateValue === undefined) {
    return '';
  }
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    if (!trimmed) return '';
    if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(trimmed)) {
      return trimmed.replace(/\//g, '-');
    }
    return trimmed;
  }
  if (typeof dateValue === 'number') {
    const numValue = dateValue;
    if (numValue < 1 || numValue > 2958465) {
      return String(numValue);
    }
    try {
      let jsDate;
      if (numValue < 60) {
        jsDate = new Date(1899, 11, 30 + numValue);
      } else if (numValue < 61) {
        jsDate = new Date(1900, 1, 28);
      } else {
        const epoch = new Date(1899, 11, 30);
        jsDate = new Date(epoch.getTime() + (numValue - 1) * 24 * 60 * 60 * 1000);
      }
      const year = jsDate.getFullYear();
      const month = String(jsDate.getMonth() + 1).padStart(2, '0');
      const day = String(jsDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Excel日期转换失败:', error, '原始值:', numValue);
      return String(numValue);
    }
  }
  if (dateValue instanceof Date) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return String(dateValue);
}

/**
 * 通用 Excel 表格校验函数
 * 检查表头是否包含必填列，以及数据行中必填列是否有空值
 * @param file Excel 文件
 * @param requiredColumns 必填列名数组
 * @returns { valid, errors } 校验结果及错误信息
 */
export function validateExcelFile(file, requiredColumns) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const result = event.target?.result;
        const workbook = XLSX.read(result, {
          type: 'array'
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: ''
        });
        if (rows.length === 0) {
          resolve({
            valid: false,
            errors: ['文件为空']
          });
          return;
        }

        // 检测第一行是否为表头
        const firstRow = (rows[0] || []).map(v => String(v).trim());
        const isHeader = requiredColumns.some(col => firstRow.includes(col));
        if (!isHeader) {
          resolve({
            valid: false,
            errors: [`表头缺少必填列：${requiredColumns.join('、')}`]
          });
          return;
        }

        // 检查缺失的必填列
        const missingColumns = requiredColumns.filter(col => !firstRow.includes(col));
        const errors = [];
        if (missingColumns.length > 0) {
          errors.push(`表头缺少必填列：${missingColumns.join('、')}`);
        }

        // 获取必填列的名称和索引
        const presentColumns = requiredColumns.filter(col => firstRow.includes(col)).map(col => ({
          name: col,
          index: firstRow.indexOf(col)
        }));
        const dataRows = rows.slice(1);
        if (dataRows.length === 0) {
          errors.push('表格无数据');
          resolve({
            valid: false,
            errors
          });
          return;
        }

        // 过滤出非空行，保留原始行号
        const nonEmptyRows = dataRows.map((row, idx) => ({
          row,
          lineNum: idx + 2
        })).filter(({
          row
        }) => row.some(v => v !== undefined && v !== null && String(v).trim() !== ''));

        // 检查非空行中必填列是否全为空
        const emptyColumns = presentColumns.filter(({
          index
        }) => nonEmptyRows.every(({
          row
        }) => {
          const v = row[index];
          return v === undefined || v === null || String(v).trim() === '';
        }));
        if (emptyColumns.length > 0) {
          errors.push(`${emptyColumns.map(c => c.name).join('、')} 数据行为空`);
        }

        // 保留有数据的列进行逐行校验
        const validColumns = presentColumns.filter(c => !emptyColumns.includes(c));

        // 检查非空行中必填列是否有空值
        for (const {
          row,
          lineNum
        } of nonEmptyRows) {
          for (const {
            index
          } of validColumns) {
            const val = row[index];
            if (val === undefined || val === null || String(val).trim() === '') {
              errors.push(`第 ${lineNum} 行数据不完整`);
              break;
            }
          }
        }
        resolve({
          valid: errors.length === 0,
          errors
        });
      } catch {
        resolve({
          valid: false,
          errors: ['文件解析失败']
        });
      }
    };
    reader.onerror = () => {
      resolve({
        valid: false,
        errors: ['文件读取失败']
      });
    };
    reader.readAsArrayBuffer(file);
  });
}